// Editor der E-Signatur (Design-Doc docs/superpowers/specs/2026-07-24-esignatur-design.md, Handoff Dokumente §6.4
// „Neuer Vorgang“): drei Schritte – Dokument & Bezug, Unterzeichner, Felder platzieren – und darunter die
// PDF-Seiten (pdfjs-dist, dynamic import). Felder sind absolut positionierte Overlays (%-Koordinaten, y von OBEN –
// Konvention geteilt mit der Edge Function `signieren`), per Pointer-Events verschieb- und größenveränderbar.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowsClockwise, Calendar, CaretDown, CheckSquare, CircleNotch, Copy, Database, FloppyDisk, Info, Lock, PaperPlaneTilt,
  PenNib, Plus, Sparkle, TextT, Trash, UploadSimple, UserCirclePlus, UserPlus, Users, WarningCircle, X, type Icon,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, DialogHuelle, ImmoButton, ObjektPicker, SchalterZeile, SearchSelect, SegmentedControl,
  type ObjektPickerOption, type SucheOption,
} from '../../../components/immobilien/ui';
import { supabase } from '../../../lib/supabase/client';
import { naechsterUnterzeichner, darfErneutSenden } from '../../../lib/signatur/vorgangAktionen';
import SignierenSeite from '../../public/SignierenSeite';
import { useIslandActions } from '../../../components/island/IslandProvider';
import type { Feld, FeldTyp, SignaturVorgang, Unterzeichner } from '../../../lib/dokumente/signaturTypen';
import { QUELLEN, ladeQuellenWerte, kontaktAnzeigename, type Bezug } from './vorbelegung';
import { bereinigeSuchbegriff } from '../../../lib/kontakte/personenSuche';
import { rubrikenAus, OHNE_RUBRIK } from '../../../lib/dokumente/signaturFilter';
import { istPdfVerschluesselt, PDF_GESCHUETZT_MELDUNG } from '../../../lib/signatur/pdfSchutz';
import { functionFehlerText } from '../../../lib/supabase/functionFehler';
import { nochOffen, unterzeichnerFarbe } from '../../../lib/dokumente/signatur';
import { kopiereAlsEntwurf } from '../../../lib/dokumente/signaturApi';
import { ladeObjekte, type AktObjekt } from '../../../lib/dokumente/useAktenschrank';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { ladePdfjs } from '../../../lib/pdf/pdfjs';

const farbeFuer = unterzeichnerFarbe;

const TYP_LABEL: Record<FeldTyp, string> = { signatur: 'Unterschrift', text: 'Text', datum: 'Datum', checkbox: 'Häkchen' };
const TYP_ICON: Record<FeldTyp, Icon> = { signatur: PenNib, text: TextT, datum: Calendar, checkbox: CheckSquare };
// Größen in % der Seite (Handoff §4: Unterschrift 30×6, Text 26×4, Datum 16×4, Häkchen 5×3,5)
const DEFAULT_GROESSE: Record<FeldTyp, { w: number; h: number }> = {
  signatur: { w: 30, h: 6 },
  text: { w: 26, h: 4 },
  datum: { w: 16, h: 4 },
  checkbox: { w: 5, h: 3.5 },
};

const MAX_DATEIGROESSE = 20 * 1024 * 1024;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const clamp = (v: number, min: number, max: number): number => (max < min ? min : Math.min(Math.max(v, min), max));

const neuerUnterzeichner = (): Unterzeichner => ({
  name: '', email: '', token: '', status: 'offen', signiert_am: null, ip: null, werte: {}, unterschriften: {},
});

const FELD_LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' };
const EINGABE: React.CSSProperties = {
  height: 44, padding: '0 14px', borderRadius: 14, border: 'none', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
  background: 'var(--bg)', color: 'var(--ink)', boxShadow: 'inset 0 0 0 1px var(--line)', minWidth: 0, width: '100%',
};
const SELECT: React.CSSProperties = { ...EINGABE, appearance: 'none', paddingRight: 36, cursor: 'pointer' };
const MINI_EINGABE: React.CSSProperties = { ...EINGABE, height: 36, borderRadius: 10, fontSize: 12, padding: '0 10px' };

function Auswahlfeld({ wert, onChange, children, label, disabled }: { wert: string; onChange: (v: string) => void; children: React.ReactNode; label: string; disabled?: boolean }) {
  return (
    <span className="relative flex items-center min-w-0" style={{ flex: 1 }}>
      <select aria-label={label} value={wert} onChange={e => onChange(e.target.value)} disabled={disabled} style={SELECT}>{children}</select>
      <CaretDown weight="bold" size={11} color="var(--ink3)" style={{ position: 'absolute', right: 14, pointerEvents: 'none' }} />
    </span>
  );
}

/** Ein Schritt mit Nummer und Verbindungslinie (Handoff Screen 16/17). */
function Schritt({ nr, titel, sub, letzter, children }: { nr: number; titel: string; sub: string; letzter?: boolean; children: React.ReactNode }) {
  return (
    <section className="flex min-w-0" style={{ gap: 14 }} aria-label={titel}>
      <div className="flex flex-col items-center shrink-0">
        <span className="immo-zahl flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 15, fontSize: 13, fontWeight: 800, background: 'var(--acc-fill)', color: 'var(--acc-ink)' }}>{nr}</span>
        {!letzter && <span style={{ flex: 1, width: 2, marginTop: 6, background: 'var(--line)', borderRadius: 1 }} />}
      </div>
      <div className="flex flex-col flex-1 min-w-0" style={{ gap: 10, paddingBottom: letzter ? 0 : 18 }}>
        <div className="flex flex-col" style={{ gap: 1 }}>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{titel}</span>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>
        </div>
        {children}
      </div>
    </section>
  );
}

function Chip({ an, onClick, children, farbe, disabled, title }: { an: boolean; onClick: () => void; children: React.ReactNode; farbe?: string; disabled?: boolean; title?: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={an} title={title} className="flex items-center cursor-pointer"
      style={{ height: 44, padding: '0 12px', borderRadius: 14, gap: 7, border: 'none', fontSize: 13, fontWeight: 700, opacity: disabled ? 0.45 : 1,
        background: an ? 'var(--bg)' : 'var(--fill2)', color: 'var(--ink)', boxShadow: an ? `inset 0 0 0 2px ${farbe ?? 'var(--acc)'}` : 'none' }}>
      {children}
    </button>
  );
}

// #583: Legacy-Build über den gemeinsamen Lader (ältere Handy-Browser).
const importPdfjs = ladePdfjs;

// ── Vektor-Struktur des PDFs extrahieren (für den KI-Feldvorschlag) ────────────
// Formulare zeichnen ihre Eingabe-Kästen, Ankreuz-Kästchen und Tabellenzellen als
// echte Rechtecke/Linien in den PDF-Operatorstrom. Die werden hier mit exakten
// Koordinaten (% der Seite, y von oben) eingesammelt — die KI muss dann nur noch
// SEMANTISCH zuordnen ("Feld gehört in Rechteck #R7"), nicht mehr Geometrie raten.
async function extrahiereStruktur(pdfDoc: any, maxSeiten: number): Promise<{
  seite: number;
  rechtecke: { x: number; y: number; w: number; h: number; klasse: 'kaestchen' | 'box' }[];
  linien: { x: number; y: number; w: number }[];
}[]> {
  const pdfjs = await importPdfjs();
  const OPS: any = (pdfjs as any).OPS;
  const ergebnis: Awaited<ReturnType<typeof extrahiereStruktur>> = [];
  const mul = (a: number[], b: number[]) => [
    a[0] * b[0] + a[2] * b[1], a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3], a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4], a[1] * b[4] + a[3] * b[5] + a[5],
  ];
  const wende = (m: number[], x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];

  for (let s = 1; s <= Math.min(pdfDoc.numPages, maxSeiten); s++) {
    try {
      const page = await pdfDoc.getPage(s);
      const vp = page.getViewport({ scale: 1 });
      const B = vp.width, H = vp.height;
      const opList = await page.getOperatorList();
      const rechteckSet = new Set<string>();
      const rechtecke: { x: number; y: number; w: number; h: number; klasse: 'kaestchen' | 'box' }[] = [];
      const linien: { x: number; y: number; w: number }[] = [];
      let ctm = [1, 0, 0, 1, 0, 0];
      const stapel: number[][] = [];

      const nimmRechteck = (x1: number, y1: number, x2: number, y2: number) => {
        const links = Math.min(x1, x2), rechts = Math.max(x1, x2);
        const unten = Math.min(y1, y2), oben = Math.max(y1, y2);
        const x = links / B * 100;
        const y = (H - oben) / H * 100; // y von oben
        const w = (rechts - links) / B * 100;
        const h = (oben - unten) / H * 100;
        if (w < 1 || h < 0.6 || w > 98 || h > 60) return;      // Deko/Rahmen raus
        const key = `${x.toFixed(1)}|${y.toFixed(1)}|${w.toFixed(1)}|${h.toFixed(1)}`;
        if (rechteckSet.has(key)) return;
        rechteckSet.add(key);
        const klasse = w <= 4 && h <= 3 ? 'kaestchen' : 'box';
        rechtecke.push({ x: r1(x), y: r1(y), w: r1(w), h: r1(h), klasse });
      };
      const r1 = (v: number) => Math.round(v * 10) / 10;

      for (let i = 0; i < opList.fnArray.length; i++) {
        const fn = opList.fnArray[i];
        const args = opList.argsArray[i];
        if (fn === OPS.save) stapel.push(ctm);
        else if (fn === OPS.restore) ctm = stapel.pop() || [1, 0, 0, 1, 0, 0];
        else if (fn === OPS.transform) ctm = mul(ctm, args as number[]);
        else if (fn === OPS.constructPath) {
          // args: [subOps[], koordinaten[], …] — defensiv parsen (Format variiert je pdfjs-Version)
          const subOps: number[] = Array.isArray(args?.[0]) ? args[0] : [];
          const ko: number[] = Array.isArray(args?.[1]) ? args[1] : [];
          let ci = 0, px = 0, py = 0;
          for (const sub of subOps) {
            if (sub === OPS.rectangle) {
              const [x, y, w, h] = ko.slice(ci, ci + 4); ci += 4;
              const p1 = wende(ctm, x, y);
              const p2 = wende(ctm, x + w, y + h);
              nimmRechteck(p1[0], p1[1], p2[0], p2[1]);
            } else if (sub === OPS.moveTo) {
              [px, py] = ko.slice(ci, ci + 2); ci += 2;
            } else if (sub === OPS.lineTo) {
              const [nx, ny] = ko.slice(ci, ci + 2); ci += 2;
              const a = wende(ctm, px, py), b = wende(ctm, nx, ny);
              // Horizontale Striche = Eingabe-/Unterschriftslinien
              if (Math.abs(a[1] - b[1]) < 1.5 && Math.abs(a[0] - b[0]) > B * 0.06) {
                linien.push({ x: r1(Math.min(a[0], b[0]) / B * 100), y: r1((H - a[1]) / H * 100), w: r1(Math.abs(a[0] - b[0]) / B * 100) });
              }
              [px, py] = [nx, ny];
            } else if (sub === OPS.curveTo) ci += 6;
            else if (sub === OPS.curveTo2 || sub === OPS.curveTo3) ci += 4;
            // closePath: keine Koordinaten
          }
        }
      }
      ergebnis.push({ seite: s, rechtecke: rechtecke.slice(0, 120), linien: linien.slice(0, 60) });
    } catch (e) {
      console.warn('Struktur-Extraktion Seite', s, 'fehlgeschlagen', e);
    }
  }
  return ergebnis;
}

interface DragZustand {
  feldId: string;
  modus: 'move' | 'resize';
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  breitePx: number;
  hoehePx: number;
}

interface FeldBoxProps {
  feld: Feld;
  farbe: string;
  ausgewaehlt: boolean;
  interaktiv: boolean;
  anzeigeWert?: string | null; // bereits eingetragener Wert (verschickte Vorgänge, read-only)
  unterzeichnerListe: Unterzeichner[];
  onPointerDownFeld: (e: React.PointerEvent<HTMLDivElement>, feld: Feld) => void;
  onPointerDownGriff: (e: React.PointerEvent<HTMLDivElement>, feld: Feld) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>, feld: Feld) => void;
  onPointerEnde: () => void;
  onSelect: (id: string) => void;
  onPatch: (id: string, patch: Partial<Feld>) => void;
  onDelete: (id: string) => void;
}

const FeldBox: React.FC<FeldBoxProps> = ({
  feld, farbe, ausgewaehlt, interaktiv, anzeigeWert, unterzeichnerListe,
  onPointerDownFeld, onPointerDownGriff, onPointerMove, onPointerEnde, onSelect, onPatch, onDelete,
}) => {
  const IconKomp = TYP_ICON[feld.typ];
  const quellenLabel = feld.quelle ? (QUELLEN.find((q) => q.wert === feld.quelle)?.label || feld.quelle) : null;
  const hatEffektivenWert = anzeigeWert != null && anzeigeWert !== '';
  const zeile: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: 'var(--ink2)', cursor: 'pointer', minHeight: 32 };
  return (
    <div
      className="absolute flex items-start select-none"
      style={{
        left: `${feld.x}%`, top: `${feld.y}%`, width: `${feld.w}%`, height: `${feld.h}%`, padding: '1px 4px', gap: 4, borderRadius: 6,
        border: `2px ${quellenLabel ? 'dashed' : 'solid'} ${farbe}`, backgroundColor: `${farbe}2b`, touchAction: 'none',
        cursor: interaktiv ? 'move' : 'default', zIndex: ausgewaehlt ? 20 : 10,
      }}
      onPointerDown={interaktiv ? (e) => onPointerDownFeld(e, feld) : undefined}
      onPointerMove={interaktiv ? (e) => onPointerMove(e, feld) : undefined}
      onPointerUp={interaktiv ? onPointerEnde : undefined}
      onPointerCancel={interaktiv ? onPointerEnde : undefined}
      onClick={(e) => { e.stopPropagation(); if (interaktiv) onSelect(feld.id); }}
    >
      {/* Quellen-Badge: zeigt, WORAN das Feld hängt (z. B. „Mieter — Vorname") */}
      {interaktiv && quellenLabel && (
        <div className="absolute flex items-center whitespace-nowrap pointer-events-none"
          style={{ top: -16, right: 0, gap: 3, padding: '1px 6px', borderRadius: 6, background: '#1a1d24', color: '#c4a77d', fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <Database size={8} weight="bold" /> {quellenLabel}
        </div>
      )}
      <IconKomp size={10} weight="bold" style={{ color: farbe, flexShrink: 0, marginTop: 1 }} />
      {feld.fixiert && <Lock size={9} weight="bold" style={{ color: farbe, flexShrink: 0, marginTop: 1 }} />}
      {anzeigeWert ? (
        <span className="truncate" style={{ fontSize: 10, fontWeight: 700, color: '#1f2937' }}>{anzeigeWert}</span>
      ) : (
        <span className="truncate" style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.03em', color: farbe }}>{feld.label}</span>
      )}

      {interaktiv && (
        <div
          onPointerDown={(e) => { e.stopPropagation(); onPointerDownGriff(e, feld); }}
          onPointerMove={(e) => { e.stopPropagation(); onPointerMove(e, feld); }}
          onPointerUp={(e) => { e.stopPropagation(); onPointerEnde(); }}
          onPointerCancel={(e) => { e.stopPropagation(); onPointerEnde(); }}
          className="absolute"
          style={{ right: -7, bottom: -7, width: 14, height: 14, borderRadius: 7, background: '#fff', border: `2px solid ${farbe}`, cursor: 'nwse-resize', touchAction: 'none' }}
        />
      )}

      {interaktiv && ausgewaehlt && (
        <div
          className="immo-glas-stark absolute flex flex-col"
          style={{ zIndex: 30, width: 240, top: 'calc(100% + 8px)', left: 0, borderRadius: 18, padding: 12, gap: 8, cursor: 'default', color: 'var(--ink)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <label className="flex flex-col" style={{ gap: 4 }}>
            <span style={FELD_LABEL}>Label</span>
            <input value={feld.label} onChange={(e) => onPatch(feld.id, { label: e.target.value })} style={MINI_EINGABE} />
          </label>
          <span className="flex items-center justify-between" style={{ fontSize: 12 }}>
            <span style={FELD_LABEL}>Typ</span><strong>{TYP_LABEL[feld.typ]}</strong>
          </span>
          {unterzeichnerListe.length > 0 && (
            <label className="flex flex-col" style={{ gap: 4 }}>
              <span style={FELD_LABEL}>Unterzeichner</span>
              <select value={feld.unterzeichner} onChange={(e) => onPatch(feld.id, { unterzeichner: Number(e.target.value) })} style={{ ...MINI_EINGABE, appearance: 'auto' }}>
                {unterzeichnerListe.map((u, i) => <option key={i} value={i}>{u.name.trim() || `Unterzeichner ${i + 1}`}</option>)}
              </select>
            </label>
          )}
          <label style={zeile}>
            <input type="checkbox" checked={feld.pflicht} onChange={(e) => onPatch(feld.id, { pflicht: e.target.checked })} /> Pflichtfeld
          </label>
          {(feld.typ === 'text' || feld.typ === 'datum') && (
            <label className="flex flex-col" style={{ gap: 4 }}>
              <span style={FELD_LABEL}>Vorausfüllen aus</span>
              <select value={feld.quelle || ''} onChange={(e) => onPatch(feld.id, { quelle: e.target.value || undefined })} style={{ ...MINI_EINGABE, appearance: 'auto' }}>
                <option value="">— Keine —</option>
                {QUELLEN.map((q) => <option key={q.wert} value={q.wert}>{q.label}</option>)}
              </select>
            </label>
          )}
          {(feld.typ === 'text' || feld.typ === 'datum') && (
            <label className="flex flex-col" style={{ gap: 4 }}>
              <span style={FELD_LABEL}>Wert (manuell)</span>
              <input value={typeof feld.wert === 'string' ? feld.wert : ''} onChange={(e) => onPatch(feld.id, { wert: e.target.value || undefined })}
                placeholder={feld.typ === 'datum' ? 'TT.MM.JJJJ' : 'Wert eintragen…'} style={MINI_EINGABE} />
            </label>
          )}
          {feld.typ === 'checkbox' && (
            <label style={zeile}>
              <input type="checkbox" checked={feld.wert === true} onChange={(e) => onPatch(feld.id, { wert: e.target.checked ? true : undefined })} /> Vorab angekreuzt
            </label>
          )}
          {feld.typ !== 'signatur' && hatEffektivenWert && unterzeichnerListe.length > 0 && (
            <label style={zeile}>
              <input type="checkbox" checked={!!feld.fixiert} onChange={(e) => onPatch(feld.id, { fixiert: e.target.checked || undefined })} />
              <Lock size={12} weight="bold" /> Fixiert – nicht änderbar
            </label>
          )}
          <button type="button" onClick={() => onDelete(feld.id)} className="flex items-center justify-center cursor-pointer"
            style={{ height: 36, gap: 6, borderRadius: 10, border: 'none', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 12, fontWeight: 800 }}>
            <Trash size={13} weight="bold" /> Feld löschen
          </button>
        </div>
      )}
    </div>
  );
};

export interface SignaturEditorProps {
  vorgang: SignaturVorgang | null;
  userProfile?: { id?: string; full_name?: string | null; email?: string | null } & Record<string, unknown>;
  onZurueck: () => void;
  /** Ungespeicherte Änderungen (für die Rückfrage beim Abbrechen). */
  onGeaendert?: (geaendert: boolean) => void;
  /** Aus Vorlage wurde ein neuer Entwurf angelegt → diesen öffnen. */
  onNeuerVorgang?: (id: string) => void;
}

type Rueckfrage =
  | { art: 'ki' } | { art: 'alleFelder' } | { art: 'erneut' }
  | { art: 'unterzeichner'; idx: number; anzahl: number; name: string; zielName: string | null };

const SignaturEditor: React.FC<SignaturEditorProps> = ({ vorgang, userProfile, onZurueck, onGeaendert, onNeuerVorgang }) => {
  const { notify } = useIslandActions();
  const { mobil } = useMobilModus();

  const [aktuellerVorgang, setAktuellerVorgang] = useState<SignaturVorgang | null>(null);
  const [titel, setTitel] = useState('');
  const [unterzeichner, setUnterzeichner] = useState<Unterzeichner[]>([]);
  const [felder, setFelder] = useState<Feld[]>([]);
  const [aktiverUnterzeichnerIndex, setAktiverUnterzeichnerIndex] = useState(0);
  const [ausgewaehlteFeldId, setAusgewaehlteFeldId] = useState<string | null>(null);
  // Werkzeug: gewählt, dann platziert ein Klick auf die Seite das Feld (Handoff §6.4 Schritt 3)
  const [werkzeug, setWerkzeug] = useState<FeldTyp | null>('signatur');

  // Vorbelegung: Bezug (Objekt/Einheit/Mieter/Eigentümer/Kontakt) + die daraus abgeleiteten Werte je Feld – siehe ./vorbelegung.ts.
  const [bezug, setBezug] = useState<Bezug>({});
  const [vorbelegung, setVorbelegung] = useState<Record<string, string>>({});
  const [bezugEinheiten, setBezugEinheiten] = useState<{ id: string; bezeichnung: string }[]>([]);
  // Zuletzt geladene Quellen-Werte – für sofortiges Befüllen beim Verknüpfen eines Unterzeichners
  const [quellenKatalog, setQuellenKatalog] = useState<Record<string, string>>({});
  const [objekte, setObjekte] = useState<AktObjekt[]>([]);

  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [seitenGroessen, setSeitenGroessen] = useState<{ w: number; h: number }[]>([]);
  const [seitenGeladen, setSeitenGeladen] = useState(false);
  const [sichtbareSeite, setSichtbareSeite] = useState(1);

  const [ladePdf, setLadePdf] = useState(false);
  const [hochladen, setHochladen] = useState(false);
  const [speichertLoading, setSpeichertLoading] = useState(false);
  const [versendenLoading, setVersendenLoading] = useState(false);
  const [selbstLoading, setSelbstLoading] = useState(false);
  // Gesetzt = Signier-Ansicht liegt als Vollbild über dem Editor.
  const [selbstToken, setSelbstToken] = useState<string | null>(null);
  const [ausfuellenLoading, setAusfuellenLoading] = useState(false);
  const [gespeichertHinweis, setGespeichertHinweis] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Hinweise aus Prüfungen und Aktionen (statt alert) – erscheinen im Fuß.
  const [meldung, setMeldung] = useState<string | null>(null);
  const [kiLaeuft, setKiLaeuft] = useState(false);
  const [frage, setFrage] = useState<Rueckfrage | null>(null);
  // Vorlage = Blaupause (PDF + Felder + Rollen), wird nie verschickt – daraus erzeugt die Liste per Kopie neue Vorgänge.
  const [istVorlage, setIstVorlage] = useState(false);
  // Rubrik zur Einordnung der Vorlage (nur relevant bei istVorlage) + Vorschläge aus bereits vergebenen Rubriken.
  const [vorlageKategorie, setVorlageKategorie] = useState('');
  const [rubrikVorschlaege, setRubrikVorschlaege] = useState<string[]>([]);
  // Quelle eines neuen Vorgangs: eigenes PDF oder Kopie einer Vorlage
  const [quelle, setQuelle] = useState<'pdf' | 'vorlage'>('pdf');
  const [vorlagen, setVorlagen] = useState<SignaturVorgang[] | null>(null);
  const [ausVorlageLaeuft, setAusVorlageLaeuft] = useState<string | null>(null);
  const [neuerTitel, setNeuerTitel] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pdfjs-Dokument (Typ kommt erst per dynamic import)
  const pdfDocRef = useRef<any>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const containerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragZustand | null>(null);
  const sichtbareSeitenSet = useRef<Set<number>>(new Set());
  const gespeichertStand = useRef<string>('');

  const readOnly = !!aktuellerVorgang && aktuellerVorgang.status !== 'entwurf';
  // Beim fertigen Dokument sind alle Werte eingebrannt – Overlays wären doppelt.
  const zeigtSigniertesPdf = !!aktuellerVorgang && aktuellerVorgang.status === 'abgeschlossen' && !!aktuellerVorgang.signiert_path;

  // Stand für „ungespeicherte Änderungen“
  const stand = JSON.stringify({ titel, felder, unterzeichner, bezug, vorbelegung, istVorlage, vorlageKategorie });
  useEffect(() => {
    if (!aktuellerVorgang || readOnly) { onGeaendert?.(false); return; }
    if (!gespeichertStand.current) gespeichertStand.current = stand;
    onGeaendert?.(stand !== gespeichertStand.current);
  }, [stand, aktuellerVorgang, readOnly, onGeaendert]);

  // Bereits eingetragener Wert eines Felds (read-only-Ansicht verschickter Vorgänge) – im Bearbeiten-Modus zeigt
  // dasselbe Overlay stattdessen die Vorbelegung an, damit der vorausgefüllte Wert direkt sichtbar ist.
  const anzeigeWertFuer = (f: Feld): string | null => {
    if (!readOnly) {
      if (f.typ === 'checkbox') return f.wert === true ? '✓' : null;
      if (typeof f.wert === 'string' && f.wert.trim()) return f.wert;
      return vorbelegung[f.id] ?? null;
    }
    const u = unterzeichner[f.unterzeichner];
    if (!u || u.status !== 'signiert') return null;
    if (f.typ === 'signatur') return u.unterschriften?.[f.id] ? '✓ signiert' : null;
    const v = u.werte?.[f.id];
    if (f.typ === 'checkbox') return v === true ? '✓' : null;
    return typeof v === 'string' && v.trim() ? v : null;
  };

  useEffect(() => { ladeObjekte().then(setObjekte).catch(() => setObjekte([])); }, []);

  // ── Bestehenden Vorgang laden (original_path -> Storage -> pdfjs) ─────────
  useEffect(() => {
    if (!vorgang) return;
    let abgebrochen = false;
    (async () => {
      setLadePdf(true);
      setFehler(null);
      try {
        // Abgeschlossene Vorgänge zeigen das FERTIGE Dokument statt des leeren Originals.
        const pdfPfad = vorgang.status === 'abgeschlossen' && vorgang.signiert_path ? vorgang.signiert_path : vorgang.original_path;
        const { data: blob, error: dlErr } = await supabase.storage.from('signaturen').download(pdfPfad);
        if (dlErr || !blob) throw new Error(dlErr?.message || 'PDF konnte nicht geladen werden.');
        const bytes = await blob.arrayBuffer();
        const pdfjs = await importPdfjs();
        const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
        if (abgebrochen) return;
        pdfDocRef.current = pdfDoc;
        gespeichertStand.current = '';
        setAktuellerVorgang(vorgang);
        setTitel(vorgang.titel);
        setUnterzeichner(vorgang.unterzeichner || []);
        setFelder(vorgang.felder || []);
        setBezug((vorgang.bezug as Bezug) || {});
        setVorbelegung(vorgang.vorbelegung || {});
        setIstVorlage(!!vorgang.ist_vorlage);
        setVorlageKategorie(vorgang.vorlage_kategorie || '');
        setPdfNumPages(pdfDoc.numPages);
        // Quellen-Werte des gespeicherten Bezugs, damit „übernehmen“ und Verknüpfen sofort greifen
        const b = (vorgang.bezug as Bezug) || {};
        if (b.objekt_id || b.mieter_id || b.eigentuemer_id || b.kontakt_id) {
          ladeQuellenWerte(b).then(k => { if (!abgebrochen) setQuellenKatalog(k); }).catch(() => undefined);
        }
      } catch (e) {
        if (!abgebrochen) setFehler((e as Error).message || 'PDF konnte nicht geladen werden.');
      } finally {
        if (!abgebrochen) setLadePdf(false);
      }
    })();
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorgang?.id]);

  // ── Rubrik-Vorschläge und Vorlagen (für „Aus Vorlage“) ───────────────────
  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      const { data } = await supabase.from('signatur_vorgaenge').select('*').eq('ist_vorlage', true).order('titel');
      if (abgebrochen || !data) return;
      const liste = data as SignaturVorgang[];
      setVorlagen(liste);
      setRubrikVorschlaege(rubrikenAus(liste.filter(v => v.vorlage_kategorie)).filter((r) => r !== OHNE_RUBRIK));
    })();
    return () => { abgebrochen = true; };
  }, []);

  // ── Seiten rendern, sobald die Seitenanzahl (und damit die Canvas-Refs) bekannt ist ──
  useEffect(() => {
    if (pdfNumPages <= 0 || !pdfDocRef.current) return;
    let abgebrochen = false;
    (async () => {
      setSeitenGeladen(false);
      const pdfDoc = pdfDocRef.current;
      const groessen: { w: number; h: number }[] = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvasRefs.current[i - 1];
        if (canvas) {
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          if (ctx) await page.render({ canvasContext: ctx, viewport, canvas }).promise;
        }
        groessen.push({ w: viewport.width, h: viewport.height });
      }
      if (abgebrochen) return;
      setSeitenGroessen(groessen);
      setSeitenGeladen(true);
    })();
    return () => { abgebrochen = true; };
  }, [pdfNumPages]);

  // ── Sichtbare Seite beobachten ──
  useEffect(() => {
    if (!seitenGeladen || !scrollRef.current) return;
    const root = scrollRef.current;
    sichtbareSeitenSet.current = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const seite = Number((entry.target as HTMLElement).dataset.seite || '0');
        if (!seite) return;
        if (entry.isIntersecting) sichtbareSeitenSet.current.add(seite);
        else sichtbareSeitenSet.current.delete(seite);
      });
      if (sichtbareSeitenSet.current.size) setSichtbareSeite(Math.min(...Array.from(sichtbareSeitenSet.current)));
    }, { root, threshold: 0.3 });
    containerRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [seitenGeladen]);

  // ── Neuer Vorgang: Datei auswählen -> Upload -> Insert ─────────────────────
  const handleDateiAuswahl = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    e.target.value = '';
    if (!file) return;
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) { setFehler('Bitte eine PDF-Datei auswählen.'); return; }
    if (file.size > MAX_DATEIGROESSE) { setFehler('Die Datei ist zu groß (max. 20 MB).'); return; }
    // Schreibgeschützte PDFs HIER abfangen – sie lassen sich am Ende nicht fertigstellen (Vorfall 2026-08-13).
    const rohBytes = new Uint8Array(await file.arrayBuffer());
    if (istPdfVerschluesselt(rohBytes)) { setFehler(PDF_GESCHUETZT_MELDUNG); return; }
    setFehler(null);
    setHochladen(true);
    const pfad = `original/${crypto.randomUUID()}.pdf`;
    try {
      const { error: upErr } = await supabase.storage.from('signaturen').upload(pfad, file, { contentType: 'application/pdf' });
      if (upErr) throw new Error(upErr.message);
      const pdfjs = await importPdfjs();
      const pdfDoc = await pdfjs.getDocument({ data: rohBytes.buffer }).promise;
      pdfDocRef.current = pdfDoc;
      const titelDefault = neuerTitel.trim() || file.name.replace(/\.pdf$/i, '');
      const { data: inserted, error: insErr } = await supabase.from('signatur_vorgaenge').insert({
        titel: titelDefault, status: 'entwurf', original_path: pfad, seiten: pdfDoc.numPages, felder: [], unterzeichner: [],
        created_by: userProfile?.id || null,
      }).select().single();
      if (insErr || !inserted) throw new Error(insErr?.message || 'Vorgang konnte nicht angelegt werden.');
      gespeichertStand.current = '';
      setAktuellerVorgang(inserted as SignaturVorgang);
      setTitel(titelDefault);
      setUnterzeichner([]);
      setFelder([]);
      setAktiverUnterzeichnerIndex(0);
      setPdfNumPages(pdfDoc.numPages);
    } catch (err) {
      // Bei fehlgeschlagenem Insert bleibt ggf. eine verwaiste Storage-Datei zurück – unkritisch.
      setFehler((err as Error).message || 'Hochladen fehlgeschlagen.');
    } finally {
      setHochladen(false);
    }
  };

  const ausVorlage = async (v: SignaturVorgang) => {
    setAusVorlageLaeuft(v.id);
    setFehler(null);
    try {
      const neu = await kopiereAlsEntwurf(v, { mitEmail: false, userId: userProfile?.id ?? null });
      if (neuerTitel.trim()) await supabase.from('signatur_vorgaenge').update({ titel: neuerTitel.trim() }).eq('id', neu.id);
      onNeuerVorgang?.(neu.id);
    } catch (e) {
      setFehler((e as Error).message || 'Vorgang konnte nicht angelegt werden.');
    } finally {
      setAusVorlageLaeuft(null);
    }
  };

  // ── Unterzeichner-Verwaltung ────────────────────────────────────────────────
  const unterzeichnerHinzufuegen = (vorgabe?: Partial<Unterzeichner>) => {
    setUnterzeichner((prev) => {
      const neu = [...prev, { ...neuerUnterzeichner(), ...vorgabe }];
      setAktiverUnterzeichnerIndex(neu.length - 1);
      return neu;
    });
  };
  const unterzeichnerAendern = (idx: number, patch: Partial<Unterzeichner>) => {
    setUnterzeichner((prev) => prev.map((u, i) => (i === idx ? { ...u, ...patch } : u)));
  };
  const entferneUnterzeichnerJetzt = (idx: number, uebertragAn: number | null) => {
    setUnterzeichner((prev) => prev.filter((_, i) => i !== idx));
    setFelder((prev) => {
      const uebertragen = uebertragAn !== null
        ? prev.map((f) => (f.unterzeichner === idx ? { ...f, unterzeichner: uebertragAn, pflicht: false } : f))
        : prev.filter((f) => f.unterzeichner !== idx);
      return uebertragen.map((f) => (f.unterzeichner > idx ? { ...f, unterzeichner: f.unterzeichner - 1 } : f));
    });
    setAktiverUnterzeichnerIndex((prev) => clamp(prev >= idx ? prev - 1 : prev, 0, Math.max(0, unterzeichner.length - 2)));
  };
  const unterzeichnerEntfernen = (idx: number) => {
    const anzahl = felder.filter((f) => f.unterzeichner === idx).length;
    if (anzahl === 0) { entferneUnterzeichnerJetzt(idx, null); return; }
    const name = unterzeichner[idx]?.name?.trim() || `Unterzeichner ${idx + 1}`;
    const ziel = unterzeichner.length > 1 ? (idx === 0 ? 1 : 0) : null;
    const zielName = ziel !== null ? (unterzeichner[ziel]?.name?.trim() || `Unterzeichner ${ziel + 1}`) : null;
    setFrage({ art: 'unterzeichner', idx, anzahl, name, zielName });
  };
  // „{Mieter} übernehmen“ / „Mich hinzufügen“
  const uebernehmbar = (['mieter', 'eigentuemer'] as const)
    .map(q => ({ q, name: quellenKatalog[`${q}.name`] ?? '', email: quellenKatalog[`${q}.email`] ?? '' }))
    .filter(x => x.name && !unterzeichner.some(u => u.quelle === x.q || (x.email && u.email.trim().toLowerCase() === x.email.toLowerCase())));
  const ichName = (userProfile?.full_name ?? '').trim();
  const ichMail = (userProfile?.email ?? '').trim();
  const ichSchonDabei = !!ichMail && unterzeichner.some(u => u.email.trim().toLowerCase() === ichMail.toLowerCase());

  // ── KI-Feldvorschlag: Text mit Koordinaten extrahieren, KI platziert Felder ──
  const fuehreKiAus = async () => {
    const pdfDoc = pdfDocRef.current;
    if (!pdfDoc || kiLaeuft) return;
    setKiLaeuft(true);
    setMeldung(null);
    try {
      // Textblöcke je Seite mit Prozent-Koordinaten (y von oben) – gleiche Konvention wie die Feld-Overlays.
      const seitenTexte: { seite: number; items: { t: string; x: number; y: number; w: number; h: number }[] }[] = [];
      for (let i = 1; i <= Math.min(pdfDoc.numPages, 12); i++) {
        const page = await pdfDoc.getPage(i);
        const vp = page.getViewport({ scale: 1 });
        const inhalt = await page.getTextContent();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pdfjs TextItem
        const items = (inhalt.items as any[]).flatMap((it) => {
          const t = String(it.str || '').trim();
          if (!t) return [];
          const hoehe = Number(it.height) || 10;
          const x = (Number(it.transform?.[4]) || 0) / vp.width * 100;
          const yOben = (vp.height - ((Number(it.transform?.[5]) || 0) + hoehe)) / vp.height * 100;
          return [{
            t: t.slice(0, 100), x: Math.round(x * 10) / 10, y: Math.round(yOben * 10) / 10,
            w: Math.round(((Number(it.width) || 0) / vp.width * 100) * 10) / 10, h: Math.round((hoehe / vp.height * 100) * 10) / 10,
          }];
        }).slice(0, 400);
        if (items.length) seitenTexte.push({ seite: i, items });
      }
      // Seiten als Bilder (aus den gerenderten Canvases, ~1100 px breit) – die Vision-KI sieht Tabellen und Kästchen.
      const seitenBilder: { seite: number; dataUrl: string }[] = [];
      for (let i = 0; i < Math.min(pdfDoc.numPages, 10); i++) {
        const quelleCanvas = canvasRefs.current[i];
        if (!quelleCanvas || !quelleCanvas.width) continue;
        const zielBreite = Math.min(1100, quelleCanvas.width);
        const skala = zielBreite / quelleCanvas.width;
        const klein = document.createElement('canvas');
        klein.width = Math.round(quelleCanvas.width * skala);
        klein.height = Math.round(quelleCanvas.height * skala);
        klein.getContext('2d')?.drawImage(quelleCanvas, 0, 0, klein.width, klein.height);
        const dataUrl = klein.toDataURL('image/jpeg', 0.7);
        if (dataUrl.length < 1_400_000) seitenBilder.push({ seite: i + 1, dataUrl });
      }
      // Vektor-Struktur (Rechtecke/Kästchen/Linien) mit exakten Koordinaten – die KI ordnet nur noch zu.
      const struktur = await extrahiereStruktur(pdfDoc, 10);
      if (!seitenTexte.length && !seitenBilder.length) { setMeldung('Im Dokument wurde kein auswertbarer Inhalt gefunden.'); return; }

      const { data, error } = await supabase.functions.invoke('signieren', { body: { aktion: 'feldvorschlag', seitenTexte, seitenBilder, struktur } });
      if (error) { setMeldung(await functionFehlerText(error, 'KI-Vorschlag fehlgeschlagen – bitte erneut versuchen.')); return; }
      if (!data?.ok) { setMeldung(data?.fehler || 'KI-Vorschlag fehlgeschlagen – bitte erneut versuchen.'); return; }

      // Rollen der KI auf Unterzeichner mappen: vorhandene wiederverwenden, fehlende mit dem Rollen-Namen anlegen.
      const rollen: string[] = data.rollen || [];
      setUnterzeichner((prev) => {
        const neu = [...prev];
        while (neu.length < rollen.length) neu.push({ ...neuerUnterzeichner(), name: rollen[neu.length] });
        return neu;
      });
      const basis = Date.now();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Antwort der Edge Function
      const neueFelder: Feld[] = (data.felder as any[]).map((f, i) => ({
        id: `f${basis}k${i}`, typ: f.typ, seite: Math.min(f.seite, pdfDoc.numPages), x: f.x, y: f.y, w: f.w, h: f.h,
        unterzeichner: Math.max(0, Math.min(f.rollenIndex ?? 0, Math.max(rollen.length - 1, 0))), label: f.label, pflicht: !!f.pflicht,
      }));
      setFelder(neueFelder);
      setAusgewaehlteFeldId(null);
    } catch (e) {
      setMeldung(`KI-Vorschlag fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setKiLaeuft(false);
    }
  };
  const handleKiVorschlag = () => { if (felder.length > 0) setFrage({ art: 'ki' }); else void fuehreKiAus(); };

  // ── Felder ────────────────────────────────────────────────────────────────
  const neuesFeld = (typ: FeldTyp, seite: number, mitteX: number, mitteY: number): Feld => {
    const { w, h } = DEFAULT_GROESSE[typ];
    return {
      id: `f${Date.now()}`, typ, seite,
      x: clamp(mitteX - w / 2, 0, 100 - w), y: clamp(mitteY - h / 2, 0, 100 - h), w, h,
      unterzeichner: Math.min(aktiverUnterzeichnerIndex, Math.max(0, unterzeichner.length - 1)), label: TYP_LABEL[typ], pflicht: true,
    };
  };
  /** Klick auf eine Seite: mit gewähltem Werkzeug ein Feld an der Klickstelle platzieren, sonst Auswahl aufheben. */
  const klickAufSeite = (e: React.MouseEvent<HTMLDivElement>, seite: number) => {
    if (readOnly || !werkzeug) { setAusgewaehlteFeldId(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const f = neuesFeld(werkzeug, seite, ((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
    setFelder((prev) => [...prev, f]);
    setAusgewaehlteFeldId(f.id);
  };
  const updateFeld = (id: string, patch: Partial<Feld>) => setFelder((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const entferneFeld = (id: string) => {
    setFelder((prev) => prev.filter((f) => f.id !== id));
    setAusgewaehlteFeldId((cur) => (cur === id ? null : cur));
  };

  // ── Drag & Resize ────────────────────────────────────────────────────────
  const starteDrag = (e: React.PointerEvent<HTMLDivElement>, feld: Feld, modus: 'move' | 'resize') => {
    if (readOnly) return;
    e.stopPropagation();
    const container = containerRefs.current[feld.seite - 1];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dragRef.current = {
      feldId: feld.id, modus, startClientX: e.clientX, startClientY: e.clientY,
      startX: feld.x, startY: feld.y, startW: feld.w, startH: feld.h, breitePx: rect.width, hoehePx: rect.height,
    };
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* Browser ohne Pointer-Capture-Support */ }
    setAusgewaehlteFeldId(feld.id);
  };
  const beiPointerBewegung = (e: React.PointerEvent<HTMLDivElement>, feld: Feld) => {
    const ds = dragRef.current;
    if (!ds || ds.feldId !== feld.id) return;
    const dxPct = ((e.clientX - ds.startClientX) / ds.breitePx) * 100;
    const dyPct = ((e.clientY - ds.startClientY) / ds.hoehePx) * 100;
    if (ds.modus === 'move') {
      updateFeld(feld.id, { x: clamp(ds.startX + dxPct, 0, Math.max(0, 100 - ds.startW)), y: clamp(ds.startY + dyPct, 0, Math.max(0, 100 - ds.startH)) });
    } else {
      updateFeld(feld.id, { w: clamp(ds.startW + dxPct, 2, Math.max(2, 100 - ds.startX)), h: clamp(ds.startH + dyPct, 2, Math.max(2, 100 - ds.startY)) });
    }
  };
  const beendeDrag = () => { dragRef.current = null; };

  // ── Speichern & Versenden ────────────────────────────────────────────────
  // `mitUnterzeichnern`: frisch geänderte Liste, die im State noch nicht angekommen ist (Selbst-Signieren).
  const entwurfSpeichern = async (mitUnterzeichnern?: Unterzeichner[]): Promise<boolean> => {
    if (!aktuellerVorgang) return false;
    setSpeichertLoading(true);
    const { error: err } = await supabase.from('signatur_vorgaenge').update({
      titel, felder, unterzeichner: mitUnterzeichnern ?? unterzeichner, seiten: pdfNumPages, ist_vorlage: istVorlage,
      vorlage_kategorie: istVorlage ? (vorlageKategorie.trim() || null) : null,
      vorbelegung, bezug, updated_at: new Date().toISOString(),
    }).eq('id', aktuellerVorgang.id);
    setSpeichertLoading(false);
    if (err) { setMeldung(`Speichern fehlgeschlagen: ${err.message}`); return false; }
    gespeichertStand.current = JSON.stringify({ titel, felder, unterzeichner: mitUnterzeichnern ?? unterzeichner, bezug, vorbelegung, istVorlage, vorlageKategorie });
    onGeaendert?.(false);
    return true;
  };
  const handleEntwurfSpeichern = async () => {
    setMeldung(null);
    if (await entwurfSpeichern()) { setGespeichertHinweis(true); setTimeout(() => setGespeichertHinweis(false), 2500); }
  };

  const handleVersenden = async () => {
    if (!aktuellerVorgang) return;
    setMeldung(null);
    if (unterzeichner.length === 0) { setMeldung('Bitte fügen Sie mindestens einen Unterzeichner hinzu.'); return; }
    if (unterzeichner.some(u => !u.name.trim() || !EMAIL_REGEX.test(u.email.trim()))) { setMeldung('Bitte tragen Sie für jeden Unterzeichner einen Namen und eine gültige E-Mail-Adresse ein.'); return; }
    for (let i = 0; i < unterzeichner.length; i++) {
      if (!felder.some((f) => f.unterzeichner === i && f.typ === 'signatur' && f.pflicht)) {
        setMeldung(`„${unterzeichner[i].name || `Unterzeichner ${i + 1}`}" benötigt mindestens ein Pflicht-Unterschriftsfeld.`); return;
      }
    }
    // Fixiert + Pflicht + leer wäre eine Sackgasse: der Unterzeichner kann das Feld nicht füllen.
    const fixiertOhneWert = felder.filter((f) => {
      if (!f.fixiert || !f.pflicht || f.typ === 'signatur') return false;
      const wert = f.wert !== undefined ? f.wert : vorbelegung[f.id];
      return !(wert === true || (typeof wert === 'string' && wert.trim() !== ''));
    });
    if (fixiertOhneWert.length > 0) { setMeldung(`Fixierte Pflichtfelder ohne Wert: ${fixiertOhneWert.map((f) => f.label).join(', ')}. Bitte Wert eintragen oder Fixierung aufheben.`); return; }
    // Manuelle Datums-Werte müssen TT.MM.JJJJ sein.
    const datumUngueltig = felder.filter((f) => f.typ === 'datum' && typeof f.wert === 'string' && f.wert.trim() !== '' && !/^\d{2}\.\d{2}\.\d{4}$/.test(f.wert.trim()));
    if (datumUngueltig.length > 0) { setMeldung(`Ungültiges Datumsformat (erwartet TT.MM.JJJJ): ${datumUngueltig.map((f) => f.label).join(', ')}`); return; }

    setVersendenLoading(true);
    if (!(await entwurfSpeichern())) { setVersendenLoading(false); return; }
    const { data, error: err } = await supabase.functions.invoke('signieren', { body: { aktion: 'versenden', vorgangId: aktuellerVorgang.id } });
    setVersendenLoading(false);
    if (err) { setMeldung(await functionFehlerText(err, 'Versand fehlgeschlagen.')); return; }
    if (!data?.ok) { setMeldung(data?.fehler || 'Versand fehlgeschlagen.'); return; }
    notify({ title: 'Dokument verschickt', tone: 'success' });
    onZurueck();
  };

  // Selbst unterschreiben: Vorgang scharf schalten OHNE Einladungsmail und die Signier-Ansicht hier öffnen
  // (User-Wunsch 2026-08-12). Die weiteren Unterzeichner bekommen ihre Einladung wie gewohnt.
  const handleSelbstSignieren = async (index = 0) => {
    if (!aktuellerVorgang) return;
    setMeldung(null);
    if (felder.length === 0) { setMeldung('Bitte platzieren Sie mindestens ein Feld auf dem Dokument.'); return; }
    setSelbstLoading(true);
    if (!readOnly) {
      // „Selbst unterschreiben" heißt: ICH bin dieser Unterzeichner – ein leerer Unterzeichner bekommt die eigenen Daten (Ticket #479).
      let liste = unterzeichner;
      const ziel = unterzeichner[index];
      if (ziel && !ziel.name?.trim() && !ziel.email?.trim() && ichName && ichMail) {
        liste = unterzeichner.map((u, i) => (i === index ? { ...u, name: ichName, email: ichMail } : u));
        setUnterzeichner(liste);
      }
      if (!(await entwurfSpeichern(liste))) { setSelbstLoading(false); return; }
    }
    const { data, error: err } = await supabase.functions.invoke('signieren', { body: { aktion: 'selbst-signieren', vorgangId: aktuellerVorgang.id, index } });
    setSelbstLoading(false);
    if (err) { setMeldung(await functionFehlerText(err, 'Öffnen fehlgeschlagen.')); return; }
    if (!data?.ok || !data?.token) { setMeldung(data?.fehler || 'Öffnen fehlgeschlagen.'); return; }
    setSelbstToken(data.token as string);
  };

  // Einladung noch einmal an den schicken, der an der Reihe ist (Ticket Björn 09/2026).
  const [erinnernLoading, setErinnernLoading] = useState(false);
  const fuehreErneutSendenAus = async () => {
    if (!aktuellerVorgang) return;
    setErinnernLoading(true);
    const { data, error: err } = await supabase.functions.invoke('signieren', { body: { aktion: 'erneut-senden', vorgangId: aktuellerVorgang.id } });
    setErinnernLoading(false);
    if (err) { setMeldung(`Versand fehlgeschlagen: ${await functionFehlerText(err, 'Unbekannter Fehler')}`); return; }
    if (!data?.ok) { setMeldung(`Versand fehlgeschlagen: ${data?.fehler || 'Unbekannter Fehler'}`); return; }
    notify({ title: 'Einladung erneut verschickt', body: `An ${data.name || ''} <${data.email}>`, tone: 'success' });
  };

  // Ausfüllen ohne Unterzeichner: prüfen, speichern, Werte serverseitig einbrennen (Aktion 'ausfuellen').
  const handleAusfuellen = async () => {
    if (!aktuellerVorgang) return;
    setMeldung(null);
    if (felder.length === 0) { setMeldung('Bitte platzieren Sie mindestens ein Feld auf dem Dokument.'); return; }
    const signaturFelder = felder.filter((f) => f.typ === 'signatur');
    if (signaturFelder.length > 0) { setMeldung(`Unterschriftsfelder erfordern den Signier-Flow: ${signaturFelder.map((f) => f.label).join(', ')}. Bitte Unterzeichner anlegen oder die Felder entfernen.`); return; }
    const effektiv = (f: Feld) => (f.wert !== undefined ? f.wert : vorbelegung[f.id]);
    const pflichtOhneWert = felder.filter((f) => {
      if (!f.pflicht) return false;
      const wert = effektiv(f);
      return !(wert === true || (typeof wert === 'string' && wert.trim() !== ''));
    });
    if (pflichtOhneWert.length > 0) { setMeldung(`Pflichtfelder ohne Wert: ${pflichtOhneWert.map((f) => f.label).join(', ')}.`); return; }
    const datumUngueltig = felder.filter((f) => f.typ === 'datum' && typeof f.wert === 'string' && f.wert.trim() !== '' && !/^\d{2}\.\d{2}\.\d{4}$/.test(f.wert.trim()));
    if (datumUngueltig.length > 0) { setMeldung(`Ungültiges Datumsformat (erwartet TT.MM.JJJJ): ${datumUngueltig.map((f) => f.label).join(', ')}`); return; }

    setAusfuellenLoading(true);
    if (!(await entwurfSpeichern())) { setAusfuellenLoading(false); return; }
    const { data, error: err } = await supabase.functions.invoke('signieren', { body: { aktion: 'ausfuellen', vorgangId: aktuellerVorgang.id } });
    setAusfuellenLoading(false);
    if (err) { setMeldung(await functionFehlerText(err, 'Ausfüllen fehlgeschlagen.')); return; }
    if (!data?.ok) { setMeldung(data?.fehler || 'Ausfüllen fehlgeschlagen.'); return; }
    notify({ title: 'Dokument ausgefüllt', tone: 'success' });
    onZurueck();
  };

  // ── Bezug (Vorbelegung) ──────────────────────────────────────────────────
  // Neuen Bezug übernehmen und alle Felder mit Datenquelle (feld.quelle) neu befüllen. Kein Auto-Save.
  const wendeBezugAn = async (neuerBezug: Bezug) => {
    setBezug(neuerBezug);
    const katalog = await ladeQuellenWerte(neuerBezug);
    setQuellenKatalog(katalog);
    const neu: Record<string, string> = {};
    felder.forEach((f) => { if (f.quelle && katalog[f.quelle] !== undefined) neu[f.id] = katalog[f.quelle]; });
    setVorbelegung(neu);
    // Unterzeichner aus dem Bezug befüllen: verknüpfte immer; unverknüpfte per Namens-Heuristik nur ohne E-Mail.
    setUnterzeichner((prev) => prev.map((u) => {
      let q = u.quelle;
      if (!q && !u.email.trim()) {
        const n = (u.name || '').toLowerCase();
        if (n.includes('mieter') && !n.includes('vermieter')) q = 'mieter';
        else if (n.includes('eigentümer') || n.includes('eigentuemer') || n.includes('vermieter')) q = 'eigentuemer';
      }
      if (!q) return u;
      const name = katalog[`${q}.name`];
      const email = katalog[`${q}.email`];
      if (!name && !email) return u;
      return { ...u, name: name || u.name, email: email || u.email };
    }));
  };

  // Einheiten des gewählten Bezugs-Objekts nachladen.
  useEffect(() => {
    if (!bezug.objekt_id) { setBezugEinheiten([]); return; }
    let abgebrochen = false;
    (async () => {
      const { data } = await supabase.from('objekt_einheiten').select('id, bezeichnung').eq('objekt_id', bezug.objekt_id as string).order('bezeichnung', { ascending: true });
      if (!abgebrochen) setBezugEinheiten(data || []);
    })();
    return () => { abgebrochen = true; };
  }, [bezug.objekt_id]);

  const sucheBezugMieter = async (q: string): Promise<SucheOption[]> => {
    const rein = bereinigeSuchbegriff(q);
    if (!rein) return [];
    const { data } = await supabase.from('mieter').select('id, vorname, nachname').or(`vorname.ilike.%${rein}%,nachname.ilike.%${rein}%`).limit(8);
    return (data || []).map((m) => ({ id: m.id as string, label: `${m.vorname || ''} ${m.nachname || ''}`.trim() }));
  };
  const sucheBezugEigentuemer = async (q: string): Promise<SucheOption[]> => {
    const rein = bereinigeSuchbegriff(q);
    if (!rein) return [];
    const { data } = await supabase.from('eigentuemer').select('id, vorname, nachname, firma_name').or(`vorname.ilike.%${rein}%,nachname.ilike.%${rein}%,firma_name.ilike.%${rein}%`).limit(8);
    return (data || []).map((e) => ({ id: e.id as string, label: ((e.firma_name as string | null)?.trim()) || `${e.vorname || ''} ${e.nachname || ''}`.trim() }));
  };
  // Sonstige Kontakte (z. B. Zahlungsempfänger eines SEPA-Mandats) – dort liegen Gläubiger-ID und Bankverbindung.
  const sucheBezugKontakte = async (q: string): Promise<SucheOption[]> => {
    const rein = bereinigeSuchbegriff(q);
    if (!rein) return [];
    const { data } = await supabase.from('kontakte').select('id, vorname, nachname, firma_name').or(`vorname.ilike.%${rein}%,nachname.ilike.%${rein}%,firma_name.ilike.%${rein}%`).limit(8);
    return (data || []).map((k) => ({ id: k.id as string, label: kontaktAnzeigename(k) })).filter((k) => k.label);
  };

  const feldAnzahlMitQuelle = felder.filter((f) => f.quelle).length;
  const objektOptionen: ObjektPickerOption[] = useMemo(() => objekte.map(o => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '), suchtext: `${o.nummer} ${o.ort}`,
  })), [objekte]);
  const offen = nochOffen({ titel, unterzeichner, felder });
  const beschaeftigt = speichertLoading || versendenLoading || ausfuellenLoading || selbstLoading;

  // ── Darstellung ──────────────────────────────────────────────────────────
  const [breit, setBreit] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 1200));
  useEffect(() => {
    const neu = () => setBreit(window.innerWidth >= 1200);
    window.addEventListener('resize', neu);
    return () => window.removeEventListener('resize', neu);
  }, []);

  const aktiverName = unterzeichner[aktiverUnterzeichnerIndex]?.name.trim() || `Unterzeichner ${aktiverUnterzeichnerIndex + 1}`;
  const statusText = (u: Unterzeichner) => (u.status === 'signiert' ? 'signiert' : u.status === 'angesehen' ? 'angesehen' : 'offen');

  const bezugZeilen = [
    bezug.objekt_name && `Objekt: ${bezug.objekt_name}`, bezug.einheit_name && `Einheit: ${bezug.einheit_name}`,
    bezug.mieter_name && `Mieter: ${bezug.mieter_name}`, bezug.eigentuemer_name && `Eigentümer: ${bezug.eigentuemer_name}`,
    bezug.kontakt_name && `Kontakt: ${bezug.kontakt_name}`,
  ].filter((z): z is string => !!z);

  const personFeld = (label: string, gewaehlt: string | null | undefined, suche: (q: string) => Promise<SucheOption[]>, setze: (o: SucheOption | null) => void) => (
    <div className="flex flex-col" style={{ gap: 6 }}>
      <span style={FELD_LABEL}>{label}</span>
      <SearchSelect suche={(q) => suche(q)} minZeichen={2} platzhalter="optional – Name suchen"
        ausgewaehlt={gewaehlt ? { id: 'gewaehlt', label: gewaehlt } : null} onEntfernen={() => setze(null)} onWahl={(o) => setze(o)} />
    </div>
  );

  const schritt1 = (
    <Schritt nr={1} titel="Dokument & Bezug" sub={aktuellerVorgang ? 'Titel und Bezug für die Vorbelegung' : 'PDF hochladen oder Vorlage wählen'}>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={FELD_LABEL}>Titel</span>
        {readOnly ? <span style={{ fontSize: 15, fontWeight: 800 }}>{titel}</span> : (
          <input value={aktuellerVorgang ? titel : neuerTitel} onChange={(e) => (aktuellerVorgang ? setTitel(e.target.value) : setNeuerTitel(e.target.value))}
            placeholder="z. B. Mietvertrag Wohnung 02" style={EINGABE} />
        )}
      </label>

      {!aktuellerVorgang && (
        <>
          <SegmentedControl<'pdf' | 'vorlage'> ariaLabel="Quelle" voll hoehe={mobil ? 44 : 36} wert={quelle} onChange={setQuelle}
            segmente={[{ key: 'pdf', label: 'PDF hochladen', icon: UploadSimple }, { key: 'vorlage', label: 'Aus Vorlage', icon: Copy }]} />
          {quelle === 'pdf' ? (
            <label className="immo-innen immo-hover-flaeche flex items-center justify-center cursor-pointer" style={{ borderRadius: 18, minHeight: 84, gap: 12, padding: 14 }}>
              <span className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--fill2)' }}>
                {hochladen ? <CircleNotch size={20} className="animate-spin" color="var(--acc)" /> : <UploadSimple weight="bold" size={20} color="var(--acc)" />}
              </span>
              <span className="flex flex-col">
                <span style={{ fontSize: 15, fontWeight: 800 }}>{hochladen ? 'Wird hochgeladen …' : 'PDF auswählen'}</span>
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>max. 20 MB</span>
              </span>
              <input type="file" accept="application/pdf" hidden disabled={hochladen} onChange={handleDateiAuswahl} />
            </label>
          ) : (
            <div className="immo-innen immo-scroll flex flex-col" style={{ borderRadius: 18, padding: 6, gap: 2, maxHeight: 280, overflowY: 'auto' }}>
              {!vorlagen && <span style={{ padding: 12, fontSize: 13, color: 'var(--ink3)' }}>Vorlagen werden geladen …</span>}
              {vorlagen?.length === 0 && <span style={{ padding: 12, fontSize: 13, color: 'var(--ink3)' }}>Noch keine Vorlagen – „Als Vorlage speichern“ legt eine an.</span>}
              {vorlagen?.map(v => (
                <button key={v.id} type="button" onClick={() => void ausVorlage(v)} disabled={!!ausVorlageLaeuft} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
                  style={{ gap: 10, minHeight: 52, padding: '6px 10px', borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
                  {ausVorlageLaeuft === v.id ? <CircleNotch size={18} className="animate-spin" color="var(--acc)" /> : <Copy weight="fill" size={18} color="var(--acc)" />}
                  <span className="flex flex-col flex-1 min-w-0">
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{v.titel}</span>
                    <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{[v.vorlage_kategorie, `${v.unterzeichner.length} Unterzeichner`, `${v.felder.length} Felder`].filter(Boolean).join(' · ')}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {fehler && <span role="alert" style={{ fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
        </>
      )}

      {aktuellerVorgang && (readOnly ? (
        <div className="flex flex-col" style={{ gap: 2, fontSize: 13, color: 'var(--ink2)' }}>
          {bezugZeilen.length ? bezugZeilen.map(z => <span key={z}>{z}</span>) : <span style={{ color: 'var(--ink3)' }}>Kein Bezug gesetzt.</span>}
        </div>
      ) : (
        <>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={FELD_LABEL}>Objekt</span>
            <div className="flex items-center" style={{ gap: 8 }}>
              <div className="flex-1 min-w-0">
                <ObjektPicker optionen={objektOptionen} wert={bezug.objekt_id ?? null} leerLabel="optional – Objekt wählen"
                  onWahl={(id) => { const o = objekte.find(x => x.id === id); void wendeBezugAn({ ...bezug, objekt_id: id, objekt_name: o ? [o.nummer, o.name].filter(Boolean).join(' ') : null, einheit_id: null, einheit_name: null }); }} />
              </div>
              {bezug.objekt_id && (
                <button type="button" aria-label="Objekt entfernen" onClick={() => void wendeBezugAn({ ...bezug, objekt_id: null, objekt_name: null, einheit_id: null, einheit_name: null })}
                  className="flex items-center justify-center cursor-pointer shrink-0" style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--ink2)' }}>
                  <X weight="bold" size={13} />
                </button>
              )}
            </div>
          </div>
          {bezug.objekt_id && (
            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={FELD_LABEL}>Einheit</span>
              <Auswahlfeld label="Einheit" wert={bezug.einheit_id || ''} onChange={(id) => {
                const name = id ? (bezugEinheiten.find((x) => x.id === id)?.bezeichnung || null) : null;
                void wendeBezugAn({ ...bezug, einheit_id: id || null, einheit_name: name });
              }}>
                <option value="">— Keine —</option>
                {bezugEinheiten.map((e) => <option key={e.id} value={e.id}>{e.bezeichnung}</option>)}
              </Auswahlfeld>
            </div>
          )}
          {personFeld('Mieter', bezug.mieter_name, sucheBezugMieter, (o) => void wendeBezugAn({ ...bezug, mieter_id: o?.id ?? null, mieter_name: o?.label ?? null }))}
          {personFeld('Eigentümer', bezug.eigentuemer_name, sucheBezugEigentuemer, (o) => void wendeBezugAn({ ...bezug, eigentuemer_id: o?.id ?? null, eigentuemer_name: o?.label ?? null }))}
          {personFeld('Kontakt (z. B. Zahlungsempfänger)', bezug.kontakt_name, sucheBezugKontakte, (o) => void wendeBezugAn({ ...bezug, kontakt_id: o?.id ?? null, kontakt_name: o?.label ?? null }))}
          <span className="flex items-center" style={{ gap: 6, fontSize: 12, color: 'var(--ink3)' }}>
            <Database size={13} weight="bold" />{feldAnzahlMitQuelle > 0 ? `${feldAnzahlMitQuelle} Felder werden aus dem Bezug vorausgefüllt` : 'Feldern im Feld-Menü eine Datenquelle zuweisen, dann füllt der Bezug sie vor.'}
          </span>
          <SchalterZeile an={istVorlage} onChange={setIstVorlage} label="Als Vorlage speichern" icon={Copy} />
          {istVorlage && (
            <label className="flex flex-col" style={{ gap: 6 }}>
              <span style={FELD_LABEL}>Rubrik</span>
              <input value={vorlageKategorie} onChange={(e) => setVorlageKategorie(e.target.value)} placeholder="z. B. Mietverwaltung" list="vorlage-rubriken" style={EINGABE} />
              <datalist id="vorlage-rubriken">{rubrikVorschlaege.map((r) => <option key={r} value={r} />)}</datalist>
            </label>
          )}
        </>
      ))}
    </Schritt>
  );

  const naechster = naechsterUnterzeichner(unterzeichner);
  const schritt2 = aktuellerVorgang && (
    <Schritt nr={2} titel="Unterzeichner" sub="Reihenfolge = Signier-Reihenfolge" letzter={readOnly}>
      {unterzeichner.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{readOnly ? 'Ohne Unterzeichner ausgefüllt.' : 'Noch keine Unterzeichner – ohne Unterzeichner lässt sich das Dokument direkt ausfüllen.'}</span>}
      {unterzeichner.map((u, idx) => readOnly ? (
        <div key={idx} className="immo-innen flex flex-col" style={{ borderRadius: 16, padding: '10px 12px', gap: 6 }}>
          <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: 5, background: farbeFuer(idx), flexShrink: 0 }} />
            <span className="flex flex-col flex-1 min-w-0">
              <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{u.name || '—'}</span>
              <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{u.email || '—'}{u.erinnert_am ? ` · erinnert am ${new Date(u.erinnert_am).toLocaleDateString('de-DE')}` : ''}</span>
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: u.status === 'signiert' ? 'var(--ok)' : u.status === 'angesehen' ? 'var(--info)' : 'var(--ink3)' }}>
              {statusText(u)}{u.status === 'signiert' && u.signiert_am ? ` · ${new Date(u.signiert_am).toLocaleDateString('de-DE')}` : ''}
            </span>
          </div>
          {/* Nur der, der an der Reihe ist: der Server lässt niemanden vordrängeln. */}
          {aktuellerVorgang.status === 'verschickt' && naechster?.index === idx && (
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              <ImmoButton art="akzent" groesse={44} icon={selbstLoading ? CircleNotch : PenNib} disabled={selbstLoading} onClick={() => void handleSelbstSignieren(idx)} title="Diesen Schritt jetzt hier im Dashboard unterschreiben">Hier unterschreiben</ImmoButton>
              {darfErneutSenden({ status: aktuellerVorgang.status, ist_vorlage: aktuellerVorgang.ist_vorlage, unterzeichner }) && (
                <ImmoButton groesse={44} icon={erinnernLoading ? CircleNotch : PaperPlaneTilt} disabled={erinnernLoading} onClick={() => setFrage({ art: 'erneut' })}>Erneut senden</ImmoButton>
              )}
            </div>
          )}
        </div>
      ) : (
        <div key={idx} className="flex flex-col" style={{ gap: 6 }} onClick={() => setAktiverUnterzeichnerIndex(idx)}>
          <div className="flex items-center" style={{ gap: 8 }}>
            <span title={idx === aktiverUnterzeichnerIndex ? 'Aktiver Unterzeichner für neue Felder' : undefined}
              style={{ width: 12, height: 12, borderRadius: 6, background: farbeFuer(idx), flexShrink: 0, boxShadow: idx === aktiverUnterzeichnerIndex ? `0 0 0 3px ${farbeFuer(idx)}55` : 'none' }} />
            <div className="grid flex-1 min-w-0" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', gap: 6 }}>
              <input aria-label={`Name Unterzeichner ${idx + 1}`} value={u.name} onChange={(e) => unterzeichnerAendern(idx, { name: e.target.value })} onFocus={() => setAktiverUnterzeichnerIndex(idx)} placeholder="Name" style={EINGABE} />
              <input aria-label={`E-Mail Unterzeichner ${idx + 1}`} type="email" value={u.email} onChange={(e) => unterzeichnerAendern(idx, { email: e.target.value })} onFocus={() => setAktiverUnterzeichnerIndex(idx)} placeholder="E-Mail" style={EINGABE} />
            </div>
            <button type="button" aria-label={`Unterzeichner ${idx + 1} entfernen`} onClick={(e) => { e.stopPropagation(); unterzeichnerEntfernen(idx); }}
              className="immo-hover-flaeche flex items-center justify-center cursor-pointer shrink-0" style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--ink2)' }}>
              <Trash weight="bold" size={15} />
            </button>
          </div>
          {/* Verknüpfung mit dem Bezug: befüllt Name + E-Mail automatisch */}
          <div style={{ paddingLeft: 20 }}>
            <Auswahlfeld label="Verknüpfen" wert={u.quelle || ''} onChange={(v) => {
              const q = (v || undefined) as 'mieter' | 'eigentuemer' | undefined;
              const patch: Partial<Unterzeichner> = { quelle: q };
              if (q && (quellenKatalog[`${q}.name`] || quellenKatalog[`${q}.email`])) {
                patch.name = quellenKatalog[`${q}.name`] || u.name;
                patch.email = quellenKatalog[`${q}.email`] || u.email;
              }
              unterzeichnerAendern(idx, patch);
            }}>
              <option value="">Verknüpfen: — manuell —</option>
              <option value="mieter">Verknüpfen: Mieter (aus Bezug)</option>
              <option value="eigentuemer">Verknüpfen: Eigentümer (aus Bezug)</option>
            </Auswahlfeld>
          </div>
        </div>
      ))}
      {!readOnly && (
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          <ImmoButton groesse={44} icon={Plus} onClick={() => unterzeichnerHinzufuegen()}>Unterzeichner</ImmoButton>
          {uebernehmbar.map(x => (
            <ImmoButton key={x.q} groesse={44} icon={UserPlus} onClick={() => unterzeichnerHinzufuegen({ name: x.name, email: x.email, quelle: x.q })}>{x.name} übernehmen</ImmoButton>
          ))}
          {ichName && ichMail && !ichSchonDabei && (
            <ImmoButton groesse={44} icon={UserCirclePlus} onClick={() => unterzeichnerHinzufuegen({ name: ichName, email: ichMail })}>Mich hinzufügen</ImmoButton>
          )}
        </div>
      )}
    </Schritt>
  );

  const schritt3 = aktuellerVorgang && !readOnly && (
    <Schritt nr={3} titel="Felder platzieren" sub="Werkzeug wählen, dann auf die Seite klicken" letzter>
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {(Object.keys(TYP_LABEL) as FeldTyp[]).map((typ) => {
          const I = TYP_ICON[typ];
          return <Chip key={typ} an={werkzeug === typ} onClick={() => setWerkzeug(w => (w === typ ? null : typ))}><I weight="bold" size={15} />{TYP_LABEL[typ]}</Chip>;
        })}
      </div>
      {unterzeichner.length > 0 && (
        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Für:</span>
          {unterzeichner.map((u, i) => (
            <Chip key={i} an={i === aktiverUnterzeichnerIndex} farbe={farbeFuer(i)} onClick={() => setAktiverUnterzeichnerIndex(i)}>
              <span style={{ width: 9, height: 9, borderRadius: 5, background: farbeFuer(i) }} />{u.name.trim() || `Unterzeichner ${i + 1}`}
            </Chip>
          ))}
        </div>
      )}
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        <ImmoButton groesse={44} icon={kiLaeuft ? CircleNotch : Sparkle} disabled={kiLaeuft || !seitenGeladen} onClick={handleKiVorschlag}>{kiLaeuft ? 'KI analysiert …' : 'Felder per KI erkennen'}</ImmoButton>
        {felder.length > 0 && <ImmoButton groesse={44} icon={Trash} onClick={() => setFrage({ art: 'alleFelder' })}>Alle Felder entfernen</ImmoButton>}
      </div>
      <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{felder.length} {felder.length === 1 ? 'Feld' : 'Felder'} platziert</span>
    </Schritt>
  );

  const fuss = aktuellerVorgang && (
    <div className="flex flex-col" style={{ gap: 10, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
      {meldung && <span role="alert" className="flex items-start" style={{ gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}><WarningCircle weight="fill" size={15} style={{ flexShrink: 0, marginTop: 2 }} />{meldung}</span>}
      {!readOnly && (
        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
          <span className="flex items-center flex-1" style={{ gap: 6, fontSize: 12, color: gespeichertHinweis ? 'var(--ok)' : 'var(--ink3)', minWidth: 160 }}>
            {gespeichertHinweis ? <>Gespeichert ✓</> : !istVorlage && unterzeichner.length > 0 && offen.length > 0 ? <><Info weight="fill" size={14} />Noch offen: {offen.join(', ')}</> : null}
          </span>
          <ImmoButton groesse={44} icon={speichertLoading ? CircleNotch : FloppyDisk} disabled={beschaeftigt} onClick={() => void handleEntwurfSpeichern()}>{istVorlage ? 'Vorlage speichern' : 'Entwurf speichern'}</ImmoButton>
          {!istVorlage && (unterzeichner.length === 0 ? (
            <ImmoButton art="akzent" groesse={44} icon={ausfuellenLoading ? CircleNotch : CheckSquare} disabled={beschaeftigt} onClick={() => void handleAusfuellen()}>Ausfüllen &amp; abschließen</ImmoButton>
          ) : (
            <>
              <ImmoButton groesse={44} icon={selbstLoading ? CircleNotch : PenNib} disabled={beschaeftigt} onClick={() => void handleSelbstSignieren(0)} title="Ohne Mailversand hier im Dashboard unterschreiben">Selbst unterschreiben</ImmoButton>
              <ImmoButton art="akzent" groesse={44} icon={versendenLoading ? CircleNotch : PaperPlaneTilt} disabled={beschaeftigt || offen.length > 0} onClick={() => void handleVersenden()}>Verschicken</ImmoButton>
            </>
          ))}
        </div>
      )}
    </div>
  );

  const seitenHinweis = readOnly
    ? `Seite ${sichtbareSeite} von ${pdfNumPages}`
    : werkzeug ? `Seite ${sichtbareSeite} · Klick platziert „${TYP_LABEL[werkzeug]}“ für ${aktiverName}` : `Seite ${sichtbareSeite} · Feld anklicken zum Bearbeiten, ziehen zum Verschieben`;

  const seiten = aktuellerVorgang && !ladePdf && !fehler && (
    <div className="flex flex-col min-w-0" style={{ gap: 8, ...(breit ? { position: 'sticky', top: 12 } : {}) }}>
      <span className="flex items-center" style={{ gap: 7, fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: werkzeug && !readOnly ? 'var(--ok)' : 'var(--ink3)' }} />{seitenHinweis}
      </span>
      <div ref={scrollRef} className="immo-scroll relative" style={{ borderRadius: 26, padding: mobil ? 10 : 18, background: 'var(--fill2)', overflow: 'auto', maxHeight: breit ? 'calc(100dvh - 120px)' : '80dvh' }}>
        {/* Canvases IMMER gemountet, sobald die Seitenzahl bekannt ist – der Render-Effekt braucht die Refs. */}
        <div className="flex flex-col items-center" style={{ gap: 18 }}>
          {Array.from({ length: pdfNumPages }).map((_, i) => {
            const seiteNr = i + 1;
            const g = seitenGroessen[i] ?? { w: 600, h: 800 };
            return (
              <div key={i} ref={(el) => { containerRefs.current[i] = el; }} data-seite={seiteNr} className="relative shrink-0"
                onClick={(e) => klickAufSeite(e, seiteNr)}
                style={{ width: '100%', maxWidth: g.w, aspectRatio: `${g.w} / ${g.h}`, background: '#fff', borderRadius: 6, boxShadow: '0 10px 30px -14px var(--shade)', cursor: !readOnly && werkzeug ? 'crosshair' : 'default' }}>
                <canvas ref={(el) => { canvasRefs.current[i] = el; }} className="block w-full h-full" style={{ borderRadius: 6 }} />
                {seitenGeladen && !zeigtSigniertesPdf && felder.filter((f) => f.seite === seiteNr).map((f) => (
                  <FeldBox key={f.id} feld={f} farbe={farbeFuer(f.unterzeichner)} ausgewaehlt={ausgewaehlteFeldId === f.id} interaktiv={!readOnly}
                    anzeigeWert={anzeigeWertFuer(f)} unterzeichnerListe={unterzeichner}
                    onPointerDownFeld={(e, feld) => starteDrag(e, feld, 'move')} onPointerDownGriff={(e, feld) => starteDrag(e, feld, 'resize')}
                    onPointerMove={beiPointerBewegung} onPointerEnde={beendeDrag} onSelect={setAusgewaehlteFeldId} onPatch={updateFeld} onDelete={entferneFeld} />
                ))}
              </div>
            );
          })}
        </div>
        {!seitenGeladen && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--fill2) 80%, transparent)' }}>
            <CircleNotch size={30} className="animate-spin" color="var(--acc)" />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      {readOnly && aktuellerVorgang && (
        <div className="immo-glas flex items-center" style={{ borderRadius: 20, padding: '12px 14px', gap: 10, fontSize: 13, color: 'var(--ink2)' }}>
          <Lock weight="fill" size={16} color="var(--acc)" />
          Dieser Vorgang ist bereits {aktuellerVorgang.status === 'verschickt' ? 'verschickt' : aktuellerVorgang.status === 'abgeschlossen' ? 'abgeschlossen' : 'abgebrochen'} – nur Ansicht, keine Bearbeitung mehr möglich.
        </div>
      )}

      {!aktuellerVorgang && !vorgang ? (
        <div className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: mobil ? 14 : 20 }}>{schritt1}</div>
      ) : ladePdf || (!aktuellerVorgang && vorgang && !fehler) ? (
        <div className="immo-glas flex items-center justify-center" style={{ borderRadius: 26, padding: 60, gap: 10, fontSize: 13, color: 'var(--ink3)' }}>
          <CircleNotch size={22} className="animate-spin" color="var(--acc)" />Dokument wird geladen …
        </div>
      ) : fehler && aktuellerVorgang === null ? (
        <div role="alert" className="immo-glas flex items-center" style={{ borderRadius: 26, padding: 20, gap: 10, color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
          <WarningCircle weight="fill" size={20} />{fehler}
        </div>
      ) : (
        <div className="grid items-start" style={{ gridTemplateColumns: breit ? 'minmax(360px, 460px) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 14 }}>
          <div className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: mobil ? 14 : 20, gap: 0 }}>
            {schritt1}{schritt2}{schritt3}
            {fuss}
          </div>
          {seiten}
        </div>
      )}

      {/* Signier-Ansicht im Dashboard – dieselbe Oberfläche wie der Link aus der Mail, nur ohne Mail.
          Per Portal, damit die Drehkarte (3D-Transform) das Vollbild nicht einschränkt. */}
      {selbstToken && createPortal(
        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 500, background: '#1a1d24' }}>
          <button type="button" onClick={() => { setSelbstToken(null); onZurueck(); }} className="fixed cursor-pointer"
            style={{ right: 16, top: 16, zIndex: 510, height: 44, padding: '0 14px', borderRadius: 14, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, fontWeight: 700 }}>
            Schließen
          </button>
          <SignierenSeite token={selbstToken} onFertig={() => { setSelbstToken(null); onZurueck(); }} />
        </div>,
        document.body,
      )}

      {frage?.art === 'ki' && (
        <BestaetigenDialog titel="Felder ersetzen?" text="Die bestehenden Felder werden durch den KI-Vorschlag ersetzt." bestaetigenLabel="Ersetzen" gefahr={false} icon={Sparkle}
          onBestaetigen={() => { setFrage(null); void fuehreKiAus(); }} onAbbrechen={() => setFrage(null)} />
      )}
      {frage?.art === 'alleFelder' && (
        <BestaetigenDialog titel="Alle Felder entfernen?" text={`${felder.length} platzierte Felder werden entfernt.`} bestaetigenLabel="Entfernen"
          onBestaetigen={() => { setFelder([]); setAusgewaehlteFeldId(null); setFrage(null); }} onAbbrechen={() => setFrage(null)} />
      )}
      {frage?.art === 'erneut' && naechster && (
        <BestaetigenDialog titel="Erinnerung senden?" gefahr={false} icon={PaperPlaneTilt} bestaetigenLabel="Erneut senden"
          text={`Einladung erneut an ${naechster.unterzeichner.name || 'Unterzeichner'} <${naechster.unterzeichner.email}> schicken. Der bisherige Signier-Link bleibt gültig.`}
          onBestaetigen={() => { setFrage(null); void fuehreErneutSendenAus(); }} onAbbrechen={() => setFrage(null)} />
      )}
      {frage?.art === 'unterzeichner' && (
        <DialogHuelle titel={`„${frage.name}“ entfernen?`} icon={Users} onSchliessen={() => setFrage(null)}
          fuss={<>
            <ImmoButton art="fuellung" groesse={44} pille={false} onClick={() => setFrage(null)} style={{ flex: 1 }}>Abbrechen</ImmoButton>
            <ImmoButton art="bad" groesse={44} pille={false} icon={Trash} onClick={() => { entferneUnterzeichnerJetzt(frage.idx, null); setFrage(null); }} style={{ flex: 1 }}>Felder löschen</ImmoButton>
            {frage.zielName && (
              <ImmoButton art="akzent" groesse={44} pille={false} icon={ArrowsClockwise} onClick={() => { entferneUnterzeichnerJetzt(frage.idx, frage.idx === 0 ? 1 : 0); setFrage(null); }} style={{ flex: 1 }}>Felder behalten</ImmoButton>
            )}
          </>}>
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>
            {frage.anzahl} {frage.anzahl === 1 ? 'Feld ist' : 'Felder sind'} diesem Unterzeichner zugeordnet.
            {frage.zielName ? ` „Felder behalten“ gibt sie an „${frage.zielName}“ – dort sind sie optional und dürfen leer bleiben.` : ' Sie werden mitgelöscht.'}
          </span>
        </DialogHuelle>
      )}
    </div>
  );
};

export default SignaturEditor;

